<template>
  <b-modal
    v-model="isOpen"
    has-modal-card
    trap-focus
    :destroy-on-hide="true"
    aria-role="dialog"
    close-button-aria-label="Close"
    aria-modal
    @close="closeModal">
    <template>
      <div class="modal-card" style="width: 800px">
        <header class="modal-card-head">
          <p class="modal-card-title">
            {{ $t('storePages.product.productStock.modal.title') }}
          </p>
          <button
            type="button"
            class="delete"
            @click="closeModal()"/>
        </header>
        <section class="modal-card-body">
          <div class="columns">
            <div class="column is-6">
              <form @submit.prevent="onEnter()">
                <b-field
                  :label="$t('storePages.product.productStock.modal.location.label')"
                  label-position="inside"
                >
                  <b-input
                    ref="location"
                    v-model="form.location"
                    :placeholder="$t('storePages.product.productStock.modal.location.placeholder')"
                    expanded />
                </b-field>
              </form>
            </div>
          </div>
          <div class="columns">
            <div class="column is-6">
              <form @submit.prevent="getProductAndSend(productQuery)">
                <b-field
                  :label="$t('storePages.product.productStock.modal.product.label')"
                  label-position="inside"
                  grouped
                >
                  <b-input
                    id="product"
                    ref="product"
                    v-model="productQuery"
                    :placeholder="$t('storePages.product.productStock.modal.product.placeholder')"
                    expanded />
                  <p class="control">
                    <b-button
                      native-type="submit"
                      type="is-primary"
                      :label="$t('storePages.product.productStock.modal.product.btnSearch')"
                      :loading="loadingProduct"
                      :disabled="loadingProduct || !productQuery"
                    ></b-button>
                  </p>
                </b-field>
              </form>
            </div>
            <div class="column is-6">
              <article class="media" v-if="productInfo">
                <div class="media-left">
                  <figure class="image is-48x48">
                    <img
                      :src="productInfo.photo || 'https://assets.integrai.com.br/store/no_image.png'"
                      :alt="productInfo.name">
                  </figure>
                </div>
                <div class="media-content">
                  <div class="content">
                    <p>
                      <strong>{{ productInfo.name }}</strong>
                      <small style="display: block">
                        #{{ productInfo.sku }} - {{ productInfo.gtin }}
                      </small>
                    </p>
                  </div>
                </div>
              </article>
            </div>
          </div>
          <div class="columns">
            <div class="column is-4">
              <b-field
                :label="$t('storePages.product.productStock.modal.type.label')"
                label-position="inside">
                <b-select
                  :placeholder="$t('storePages.product.productStock.modal.type.placeholder')"
                  v-model="form.type"
                  :disabled="loadingModal"
                  expanded>
                  <option value="INPUT">
                    {{ $t('storePages.product.productStock.modal.type.options.INPUT') }}
                  </option>
                  <option value="OUTPUT">
                    {{ $t('storePages.product.productStock.modal.type.options.OUTPUT') }}
                  </option>
                  <option value="BALANCE">
                    {{ $t('storePages.product.productStock.modal.type.options.BALANCE') }}
                  </option>
                </b-select>
              </b-field>
            </div>
            <div class="column is-3">
              <b-field
                :label="$t('storePages.product.productStock.modal.quantity.label')"
                label-position="inside">
                <b-input
                  type="number"
                  :placeholder="$t('storePages.product.productStock.modal.type.placeholder')"
                  v-model="form.quantity"
                  :disabled="loadingModal">
                </b-input>
              </b-field>
            </div>
            <div class="column is-5">
              <b-switch v-model="isAutomatic">
                {{ $t('storePages.product.productStock.modal.isAutomatic.label') }}
              </b-switch>
            </div>
          </div>

          <div class="columns">
            <div class="column">
              <b-field
                :label="$t('storePages.product.productStock.modal.observation.label')"
                label-position="inside">
                <b-input
                  type="textarea"
                  :placeholder="$t('storePages.product.productStock.modal.observation.placeholder')"
                  v-model="form.observation"
                  :disabled="loadingModal">
                </b-input>
              </b-field>
            </div>
          </div>
        </section>
        <footer class="modal-card-foot">
          <b-button
            :label="$t('pageFlow.manage.buttons.cancel')"
            :disabled="loadingModal"
            @click="closeModal()" />

          <b-button
            :label="$t('pageFlow.manage.buttons.save')"
            :loading="loadingModal"
            :disabled="!formReady"
            type="is-primary"
            @click="onSubmit()" />
        </footer>
      </div>
    </template>
  </b-modal>
</template>
<script>
import { createNamespacedHelpers } from 'vuex';

const { mapState, mapActions } = createNamespacedHelpers('product-stock');

export default {
  name: 'ig-new-product-stock',
  props: ['open'],
  data() {
    return {
      isOpen: false,
      productQuery: null,
      isAutomatic: true,
      form: {
        type: 'INPUT',
        quantity: 1,
        location: null,
      },
    };
  },
  watch: {
    open(value) {
      this.isOpen = value;

      this.$nextTick(() => {
        if (value && this.$refs.location) {
          this.$refs.location.focus();
        }
      });
    },
  },
  computed: {
    ...mapState([
      'loadingProduct',
      'loadingModal',
      'productInfo',
    ]),
    formReady() {
      const quantity = parseInt(this.form.quantity, 10);
      return this.productInfo && this.form.type && quantity > 0;
    },
  },
  methods: {
    ...mapActions([
      'searchProduct',
      'saveProductStock',
    ]),
    async getProductAndSend(query) {
      await this.searchProduct(query);

      if (this.isAutomatic) {
        this.onSubmit();
      }
    },
    async onSubmit() {
      const defaultObservation = {
        INPUT: 'Entrada de estoque',
        OUPUT: 'Saída de estoque',
        BALANCE: 'Saldo de estoque',
      }[this.form.type];

      await this.saveProductStock({
        ...this.form,
        product: this.productInfo.id,
        productSku: this.productInfo.sku,
        productGtin: this.productInfo.gtin,
        observation: this.form.observation || defaultObservation,
      });

      this.productQuery = null;
      this.$refs.product.focus();

      this.cleanForm();
    },
    closeModal() {
      this.$emit('close');
      this.cleanForm();
    },
    cleanForm() {
      this.form = {
        ...this.form,
        quantity: 1,
        observation: null,
      };
    },
    onEnter() {
      this.$refs.product.focus();
    },
  },
};
</script>
